import type { CompanyProfile } from "../lib/evaluation/profile";
import { DEFAULT_COMPANY_PROFILE } from "../lib/evaluation/profile";

export function cloneDefaultCompanyProfile(): CompanyProfile {
  return structuredClone(DEFAULT_COMPANY_PROFILE);
}

export function listToCsv(values: readonly string[]): string {
  return values.join(", ");
}

export function csvToList(value: string): string[] {
  return value
    .split(/[,，\n]/u)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function formatRevenueManwon(amountKrw: number): string {
  return String(Math.round(amountKrw / 10_000));
}

export function parseRevenueManwon(value: string): number {
  const parsed = Number(value.replace(/,/gu, "").trim());
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 10_000);
}

export interface CompanyProfileDraft {
  name: string;
  types: string;
  province: string;
  city: string;
  employeeCount: string;
  businessYears: string;
  annualRevenueManwon: string;
  capabilities: string;
  targetMarkets: string;
  regionsOfInterest: string;
  partnerships: string;
}

export function profileToDraft(profile: CompanyProfile): CompanyProfileDraft {
  return {
    name: profile.company.name,
    types: listToCsv(profile.company.types),
    province: profile.company.headquarters.province,
    city: profile.company.headquarters.city,
    employeeCount: String(profile.company.employeeCount),
    businessYears: String(profile.company.businessYears),
    annualRevenueManwon: formatRevenueManwon(profile.company.annualRevenueKrw),
    capabilities: listToCsv(profile.capabilities),
    targetMarkets: listToCsv(profile.targetMarkets),
    regionsOfInterest: listToCsv(profile.regionsOfInterest),
    partnerships: listToCsv(profile.partnerships),
  };
}

export function draftToProfile(draft: CompanyProfileDraft, previous?: CompanyProfile | null): CompanyProfile {
  const employeeCount = Math.max(0, Math.floor(Number(draft.employeeCount) || 0));
  const businessYears = Math.max(0, Math.floor(Number(draft.businessYears) || 0));
  return {
    version: previous?.version ?? DEFAULT_COMPANY_PROFILE.version,
    verifiedAt: previous?.verifiedAt ?? DEFAULT_COMPANY_PROFILE.verifiedAt,
    company: {
      name: draft.name.trim() || DEFAULT_COMPANY_PROFILE.company.name,
      types: csvToList(draft.types),
      headquarters: {
        province: draft.province.trim() || DEFAULT_COMPANY_PROFILE.company.headquarters.province,
        city: draft.city.trim() || DEFAULT_COMPANY_PROFILE.company.headquarters.city,
      },
      employeeCount,
      businessYears,
      annualRevenueKrw: parseRevenueManwon(draft.annualRevenueManwon),
    },
    capabilities: csvToList(draft.capabilities),
    targetMarkets: csvToList(draft.targetMarkets),
    regionsOfInterest: csvToList(draft.regionsOfInterest),
    partnerships: csvToList(draft.partnerships),
    constraints: { autoExternalActions: false },
  };
}
